import React from 'react';
import { HDatetimePicker, HDatetimePickerProps } from './form/h-date-time-picker';
import { HElement, HElementProps } from './form/h-element';
import { HInput, HInputProps } from './form/h-input';
import { HLabel, HLabelProps } from './form/h-label';
import { HSelect, HSelectProps } from './form/h-select';
import { HSpan, HSpanProps } from './form/h-span';
import { HTextarea, HTextareaProps } from './form/h-text-area';
import { getSeqId } from 'df-helpers/dist/get-seq-id';
import { ReactModel } from '../react-model/react-model';

type TFuncUI<T> = (scope: ModelUI<T>, index: number) => React.ReactElement;
type KS<T> = Extract<keyof T, string>;

export class ModelUI<T> {
	constructor(public model: ReactModel<T>) {
		this.HInput = this.HInput.bind(this);
		this.HSelect = this.HSelect.bind(this);
		this.HTextarea = this.HTextarea.bind(this);
		this.HLabel = this.HLabel.bind(this);
		this.HSpan = this.HSpan.bind(this);
		this.HElement = this.HElement.bind(this);
		this.HDatetimePicker = this.HDatetimePicker.bind(this);
	}

	public HInput(props: Omit<HInputProps<T>, 'model'>) {
		return <HInput {...props} model={this.model} />;
	}
	public HSelect(props: Omit<HSelectProps<T>, 'model'>) {
		return <HSelect {...props} model={this.model} />;
	}
	public HTextarea(props: Omit<HTextareaProps<T>, 'model'>) {
		return <HTextarea {...props} model={this.model} />;
	}
	public HLabel(props: Omit<HLabelProps<T>, 'model'>) {
		return <HLabel {...props} model={this.model} />;
	}
	public HSpan(props: Omit<HSpanProps<T>, 'model'>) {
		return <HSpan {...props} model={this.model} />;
	}
	public HElement(props: Omit<HElementProps<T>, 'model'>) {
		return <HElement {...props} model={this.model} />;
	}
	public HDatetimePicker(props: Omit<HDatetimePickerProps<T>, 'model'>) {
		return <HDatetimePicker {...props} model={this.model} />;
	}

	public render(render_func: TFuncUI<T extends any[] ? T[0] : T>, array_item_key: (T extends any[] ? KS<T[0]> : KS<T>) | '$index'): React.ReactElement;
	public render(render_func: TFuncUI<T>, array_item_key?: any): React.ReactElement;
	public render(render_func: any, array_item_key?: any): React.ReactElement {
		if (array_item_key === undefined) return render_func(this, 0);
		else {
			const state = (this.model as any as ReactModel<any[]>).getState();
			return (
				<>
					{state.map((item, index) => {
						//fromState or fromModel is required, so that the same instance of ModelUI will
						//render the component, and the DOM component will not be re-created
						//otherwise, you will see the input box to lost focus
						const scope = this.model.no_freeze ? ModelUI.fromState(item) : ModelUI.fromModel(this.model.getModel(index as any));
						// const scope = ModelUI.fromState(item); //we have
						return <React.Fragment key={getHashKey(item, index, array_item_key!)}>{render_func(scope, index)}</React.Fragment>;
					})}
				</>
			);
		}
	}

	public static fromState<T>(obj: T): ModelUI<T> {
		if (obj && typeof obj === 'object') {
			return getObjModelUI(obj, false);
		} else {
			return new ModelUI(new ReactModel({ model: obj }));
		}
	}
	public static fromModel<T>(model: ReactModel<T>): ModelUI<T> {
		const m = model as any as { __model_ui_: ModelUI<T> };
		if (!m.__model_ui_) {
			m.__model_ui_ = new ModelUI(model);
		}

		return m.__model_ui_;
	}
}

function getHashKey(value: any, index: number, array_item_key: true | string | '$index') {
	if (array_item_key === '$index') {
		return index;
	} else if (array_item_key === true) {
		return getSeqId('i-');
	} else return value[array_item_key];
}

const obj_map = new WeakMap();

/**get an object id, which is a number grows sequentially */
function getObjModelUI<T extends {}>(obj: T, is_array: boolean): any {
	var v = obj_map.get(obj);
	if (!v) {
		const model = new ReactModel({ model: obj as any, no_freeze: true });
		v = new ModelUI(model);
		obj_map.set(obj, v);
	}
	return v;
}

//------------------------------------------------------------------------------
//
//------------------------------------------------------------------------------
